import { inject, Injectable } from '@angular/core';
import {
  type CreateLearningResourceFileInput,
  type CreateLearningResourceFileMutation,
  type CreateLearningResourceFileMutationVariables,
  type DestroyLearningResourceFileInput,
  type DestroyLearningResourceFileMutation,
  type DestroyLearningResourceFileMutationVariables,
  type LearningResourceFileQuery,
  type LearningResourceFileQueryVariables,
  type UpdateLearningResourceFileInput,
  type UpdateLearningResourceFileMutation,
  type UpdateLearningResourceFileMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { map } from 'rxjs';

import { SharedGraphQlService } from '../../../services/shared-graphql.service';
import { assertGraphqlDataNode } from '../../../utils/type-helpers';
import { learningResourceFileMinimal } from '../learning-resource-file.minimal';
import { learningResourceSetQuery } from './shared-learning-resource.api';

@Injectable({ providedIn: 'root' })
export class SharedLearningResourceFileApi {
  private readonly sharedGraphQlService = inject(SharedGraphQlService);

  watchLearningResourceFile(learningResourceFileId: string) {
    return this.sharedGraphQlService
      .watchQuery<LearningResourceFileQuery, LearningResourceFileQueryVariables>({
        query: gql`
          query LearningResourceFile($learningResourceFileId: ID!) {
            node(id: $learningResourceFileId) {
              ... on LearningResourceFile {
                ...LearningResourceFileMinimal
              }
            }
          }
          ${learningResourceFileMinimal}
        `,
        variables: {
          learningResourceFileId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'LearningResourceFile')));
  }

  createLearningResourceFile(input: CreateLearningResourceFileInput) {
    return (
      this.sharedGraphQlService
        .mutateV2<CreateLearningResourceFileMutation, CreateLearningResourceFileMutationVariables>({
          mutation: gql`
            mutation CreateLearningResourceFile($input: CreateLearningResourceFileInput!) {
              createLearningResourceFile(input: $input) {
                errors {
                  message
                  path
                }
                learningResourceFile {
                  ...LearningResourceFileMinimal
                }
              }
            }
            ${learningResourceFileMinimal}
          `,
          variables: { input },
          refetchQueries: [
            {
              query: learningResourceSetQuery,
              variables: { learningResourceSetId: input.learningResourceSetId },
            },
          ],
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createLearningResourceFile.learningResourceFile))
    );
  }

  updateLearningResourceFile(input: UpdateLearningResourceFileInput) {
    return (
      this.sharedGraphQlService
        .mutateV2<UpdateLearningResourceFileMutation, UpdateLearningResourceFileMutationVariables>({
          mutation: gql`
            mutation UpdateLearningResourceFile($input: UpdateLearningResourceFileInput!) {
              updateLearningResourceFile(input: $input) {
                errors {
                  message
                  path
                }
                learningResourceFile {
                  ...LearningResourceFileMinimal
                }
              }
            }
            ${learningResourceFileMinimal}
          `,
          variables: { input },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateLearningResourceFile.learningResourceFile))
    );
  }

  destroyLearningResourceFile(input: DestroyLearningResourceFileInput, learningResourceSetId: string) {
    return this.sharedGraphQlService.mutateV2<
      DestroyLearningResourceFileMutation,
      DestroyLearningResourceFileMutationVariables
    >({
      mutation: gql`
        mutation DestroyLearningResourceFile($input: DestroyLearningResourceFileInput!) {
          destroyLearningResourceFile(input: $input) {
            errors {
              message
              path
            }
          }
        }
      `,
      variables: {
        input,
      },
      refetchQueries: [
        {
          query: learningResourceSetQuery,
          variables: { learningResourceSetId },
        },
      ],
    });
  }
}
